import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  Badge,
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Flex,
  Icon,
  Text,
} from '@kognitos/lattice';

import { useAssetConfigRailSection } from './AssetConfigRail';

/**
 * Props for {@link AssetConfigRailSection}.
 */
export interface IAssetConfigRailSectionProps {
  title: string;
  /**
   * Stable, untranslated id: the rail item that opens this section, and the
   * `data-testid` suffix. Kept apart from `title` so it doesn't change with
   * the locale.
   */
  name: string;
  /** Defaults to `asset-config-rail-section-<name>`. */
  'data-testid'?: string;
  count?: number;
  defaultOpen?: boolean;
  /** Makes the block controlled; `defaultOpen` is ignored when set. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Sits at the far end of the header row, after the collapse control. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * A collapsible section of the asset workspace's config rail: title, optional
 * count and action (normally a "+"). It opens when its icon is clicked on the
 * collapsed rail.
 */
export function AssetConfigRailSection({
  title,
  name,
  count,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  action,
  children,
  'data-testid': dataTestId,
}: IAssetConfigRailSectionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // Clicking this section's icon on the collapsed asset workspace rail opens it.
  const openRequest = useAssetConfigRailSection(name);
  // Starts at 0: the rail mounts sections on expand, so an icon click arrives
  // as a request already pending at mount.
  const [handledOpenRequest, setHandledOpenRequest] = useState(0);
  if (handledOpenRequest !== openRequest) {
    setHandledOpenRequest(openRequest);
    if (openRequest > 0) setUncontrolledOpen(true);
  }
  // A controlled parent hears about the request after commit, never mid-render.
  const onOpenChangeRef = useRef(onOpenChange);
  // eslint-disable-next-line no-restricted-syntax -- reason: keep the latest callback for the open-request effect
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  });
  const notifiedOpenRequest = useRef(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line no-restricted-syntax -- reason: notify the parent and scroll to the section the user asked to open
  useEffect(() => {
    if (openRequest <= 0) return;
    if (notifiedOpenRequest.current !== openRequest) {
      notifiedOpenRequest.current = openRequest;
      onOpenChangeRef.current?.(true);
    }
    sectionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [openRequest]);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      {/* The hover tint lives on the block, not on the header button, so the
       * whole block reads as one surface instead of a header pill sitting on a
       * lighter body. */}
      {/* align="stretch" is explicit: lattice Flex defaults column children to
       * `items-start`, which sizes each card to its own content — the cards
       * then look like a ragged set of different widths. */}
      <Flex
        ref={sectionRef}
        direction="column"
        align="stretch"
        width="full"
        className="overflow-hidden rounded-xl bg-secondary/50 transition-colors hover:bg-secondary"
        data-testid={dataTestId ?? `asset-config-rail-section-${name}`}
      >
        <Flex align="center" width="full" className="h-11 shrink-0 pr-2">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-11 min-w-0 flex-1 justify-start gap-1.5 rounded-none px-3 !bg-transparent hover:!bg-transparent"
            >
              <Text level="small" weight="medium" className="truncate">
                {title}
              </Text>
              {count !== undefined && (
                <Badge variant="secondary" className="tabular-nums">
                  {count}
                </Badge>
              )}
              <Icon
                type="ChevronDown"
                size="xs"
                className={cn(
                  'shrink-0 text-muted-foreground transition-transform duration-300 ease-in-out motion-reduce:transition-none',
                  !open && '-rotate-90',
                )}
              />
            </Button>
          </CollapsibleTrigger>
          {action && (
            <Flex
              align="center"
              className="shrink-0"
              /* Spec: using a block's "+" also expands it. */
              onClick={() => setOpen(true)}
            >
              {action}
            </Flex>
          )}
        </Flex>
        <CollapsibleContent className="w-full overflow-hidden duration-300 ease-in-out data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none">
          {/* `align="stretch"` for the same reason as the block above: this is
           * its own column flex, so on lattice's `items-start` default every
           * card sizes to its own text and a block of two reads as two
           * different widths. */}
          <Flex
            direction="column"
            align="stretch"
            gap={2}
            width="full"
            className="px-2 pb-2"
          >
            {children}
          </Flex>
        </CollapsibleContent>
      </Flex>
    </Collapsible>
  );
}
